import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, printInvoice } from "@/lib/billing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Printer, Wallet, FileInput } from "lucide-react";
import { toast } from "sonner";
import { PaymentDialog, StatusBadge } from "@/components/InvoicePayment";
import { CompanyDialog } from "@/components/CompanyDialog";
import { SendMailDialog, MailButton, mailTargets } from "@/components/SendMail";
import { LineItemsEditor, InvoiceHeaderFields, NotesField, useBillingData, emptyInvoice, newLine } from "@/components/InvoiceForm";

export default function VendorInvoices() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { parties, items, reload } = useBillingData();
  const [invoices, setInvoices] = useState([]);
  const [inwards, setInwards] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("amount");
  const [f, setF] = useState({ ...emptyInvoice(), amount: "", gst_pct: "18", inward_ids: [] });
  const [lines, setLines] = useState([newLine()]);
  const [payFor, setPayFor] = useState(null);
  const [mailFor, setMailFor] = useState(null);

  const load = () => {
    api.get("/vendor-invoices").then((r) => setInvoices(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/billing/summary").then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (open) api.get("/movements", { params: { type: "inward" } }).then((r) => setInwards(r.data)).catch(() => {}); }, [open]);

  const toggleInward = (id) => setF({ ...f, inward_ids: f.inward_ids.includes(id) ? f.inward_ids.filter((x) => x !== id) : [...f.inward_ids, id] });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.vendor_id) return toast.error("Select a vendor");
    if (mode === "items" && lines.some((l) => !l.item_id)) return toast.error("Select an item on every line");
    try {
      const r = await api.post("/vendor-invoices", {
        invoice_no: f.invoice_no, date: f.date, due_date: f.due_date, vendor_id: f.vendor_id, notes: f.notes, inward_ids: f.inward_ids,
        amount: mode === "amount" ? parseFloat(f.amount || 0) : 0, gst_pct: parseFloat(f.gst_pct || 0),
        items: mode === "items" ? lines.map((l) => ({ item_id: l.item_id, description: l.description, quantity: parseFloat(l.quantity), rate: parseFloat(l.rate), gst_pct: parseFloat(l.gst_pct) })) : [],
      });
      toast.success(`Vendor invoice ${r.data.invoice_no} saved`);
      setOpen(false); setF({ ...emptyInvoice(), amount: "", gst_pct: "18", inward_ids: [] }); setLines([newLine()]); load(); reload();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (id) => {
    try { await api.delete(`/vendor-invoices/${id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const vendors = parties.filter((p) => p.party_type !== "customer");
  const amt = parseFloat(f.amount || 0), gstAmt = amt * parseFloat(f.gst_pct || 0) / 100;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="vendor-invoices-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Vendor Invoices</h1>
          <p className="text-muted-foreground mt-1">Purchase bills linked to inward entries, with vendor payments.</p>
        </div>
        <div className="flex gap-2">
        <CompanyDialog />
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) reload(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-vendor-invoice-btn" className="bg-amber-500 hover:bg-amber-600 text-white gap-2"><Plus className="h-4 w-4" /> Vendor Invoice</Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Vendor Invoice</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <InvoiceHeaderFields f={f} setF={setF} parties={vendors} partyKey="vendor_id" partyLabel="Vendor" prefix="vinv" />
              <div className="space-y-2">
                <Label>Link Inward Entries <span className="text-muted-foreground text-xs">(by Inward No)</span></Label>
                <div className="max-h-36 overflow-y-auto rounded-lg border border-border/60 p-2 space-y-1" data-testid="vinv-inward-list">
                  {inwards.length === 0 ? <p className="text-xs text-muted-foreground px-1">No inward entries yet.</p> : inwards.map((m) => (
                    <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white/5 rounded px-1 py-0.5">
                      <Checkbox data-testid={`vinv-inward-${m.id}`} checked={f.inward_ids.includes(m.id)} onCheckedChange={() => toggleInward(m.id)} />
                      <span className="font-mono-sku text-xs text-sky-300 w-24">{m.inward_no}</span>
                      <span className="flex-1 truncate">{m.item_name} · {m.quantity} {m.unit}</span>
                      <span className="text-xs text-muted-foreground">{m.date}{m.party ? ` · ${m.party}` : ""}</span>
                    </label>))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant={mode === "amount" ? "default" : "outline"} data-testid="vinv-mode-amount" onClick={() => setMode("amount")} className="h-7">Enter Amount</Button>
                <Button type="button" size="sm" variant={mode === "items" ? "default" : "outline"} data-testid="vinv-mode-items" onClick={() => setMode("items")} className="h-7">Itemised</Button>
              </div>
              {mode === "amount" ? (
                <div className="grid grid-cols-3 gap-4 items-end">
                  <div className="space-y-2"><Label>Taxable Amount (₹)</Label>
                    <Input data-testid="vinv-amount-input" type="number" step="0.01" min="0" required value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></div>
                  <div className="space-y-2"><Label>GST %</Label>
                    <Select value={f.gst_pct} onValueChange={(v) => setF({ ...f, gst_pct: v })}>
                      <SelectTrigger data-testid="vinv-gst-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{[0, 5, 12, 18, 28].map((g) => <SelectItem key={g} value={String(g)}>{g}%</SelectItem>)}</SelectContent>
                    </Select></div>
                  <p className="text-sm text-muted-foreground pb-2">Total <b className="text-amber-400 font-mono-sku" data-testid="vinv-grand-total">{fmt(amt + gstAmt)}</b></p>
                </div>
              ) : <LineItemsEditor lines={lines} setLines={setLines} items={items} prefix="vinv" />}
              <NotesField f={f} setF={setF} prefix="vinv" />
              <DialogFooter><Button type="submit" data-testid="vinv-submit-btn" className="bg-amber-500 hover:bg-amber-600 text-white">Save Vendor Invoice</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-card border-border"><CardContent className="p-4"><p className="text-xs uppercase text-muted-foreground font-mono-sku">Total Purchases</p><p className="text-2xl font-extrabold" data-testid="vinv-total">{fmt(summary.purchase_total)}</p><p className="text-xs text-muted-foreground">{summary.purchase_count} bills</p></CardContent></Card>
          <Card className="bg-emerald-500/10 border-emerald-500/20"><CardContent className="p-4"><p className="text-xs uppercase text-muted-foreground font-mono-sku">Paid</p><p className="text-2xl font-extrabold text-emerald-400">{fmt(summary.purchase_paid)}</p></CardContent></Card>
          <Card className="bg-amber-500/10 border-amber-500/20"><CardContent className="p-4"><p className="text-xs uppercase text-muted-foreground font-mono-sku">Payable (Due)</p><p className="text-2xl font-extrabold text-amber-400" data-testid="vinv-payable">{fmt(summary.payable)}</p></CardContent></Card>
        </div>
      )}

      <Card className="bg-card border-border"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-left text-muted-foreground">
            <th className="px-4 py-3 font-medium">Bill No</th><th className="px-4 py-3 font-medium">Date</th><th className="px-4 py-3 font-medium">Vendor</th>
            <th className="px-4 py-3 font-medium">Inward No</th><th className="px-4 py-3 font-medium text-right">Taxable</th><th className="px-4 py-3 font-medium text-right">GST</th>
            <th className="px-4 py-3 font-medium text-right">Total</th><th className="px-4 py-3 font-medium text-right">Balance</th><th className="px-4 py-3 font-medium">Due</th><th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody data-testid="vendor-invoices-table-body">
            {invoices.length === 0 ? (
              <tr><td colSpan={11} className="px-4 py-12 text-center text-muted-foreground"><FileInput className="h-10 w-10 mx-auto mb-2 opacity-40" />No vendor invoices yet.</td></tr>
            ) : invoices.map((inv) => (
              <tr key={inv.id} className="border-b border-border/50 hover:bg-white/5" data-testid={`vendor-invoice-row-${inv.id}`}>
                <td className="px-4 py-3 font-mono-sku font-semibold text-amber-300">{inv.invoice_no}</td>
                <td className="px-4 py-3 font-mono-sku text-xs">{inv.date}</td>
                <td className="px-4 py-3 font-medium">{inv.vendor_name}</td>
                <td className="px-4 py-3 font-mono-sku text-xs text-sky-300">{inv.inward_nos?.filter(Boolean).join(", ") || "—"}</td>
                <td className="px-4 py-3 text-right font-mono-sku">{fmt(inv.subtotal)}</td>
                <td className="px-4 py-3 text-right font-mono-sku text-muted-foreground">{fmt(inv.gst_total)}</td>
                <td className="px-4 py-3 text-right font-mono-sku font-semibold">{fmt(inv.total)}</td>
                <td className="px-4 py-3 text-right font-mono-sku text-amber-400" data-testid={`vendor-invoice-balance-${inv.id}`}>{inv.balance > 0 ? fmt(inv.balance) : "—"}</td>
                <td className="px-4 py-3 font-mono-sku text-xs text-muted-foreground">{inv.due_date || "—"}</td>
                <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {inv.balance > 0 && <Button size="sm" variant="ghost" data-testid={`vendor-invoice-pay-${inv.id}`} onClick={() => setPayFor(inv)} className="h-8 gap-1 text-emerald-400 hover:bg-emerald-500/10"><Wallet className="h-4 w-4" /> Pay</Button>}
                  <Button size="icon" variant="ghost" data-testid={`vendor-invoice-print-${inv.id}`} onClick={() => printInvoice(inv)} className="h-8 w-8"><Printer className="h-4 w-4" /></Button>
                  <MailButton testid={`vendor-invoice-mail-${inv.id}`} onClick={() => setMailFor(mailTargets.vendorInvoice(inv))} />
                  {isAdmin && <Button size="icon" variant="ghost" data-testid={`vendor-invoice-delete-${inv.id}`} onClick={() => del(inv.id)} className="h-8 w-8 text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-4 w-4" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>

      <PaymentDialog invoice={payFor} endpoint="/vendor-invoices" label="Vendor Payment" onClose={() => setPayFor(null)} onDone={() => { setPayFor(null); load(); }} />
      <SendMailDialog target={mailFor} onClose={() => setMailFor(null)} />
    </div>
  );
}
